import { useState, useRef, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Send } from "lucide-react";
import { useToast } from "../ui/use-toast";
import { MyTransactionItem } from "../../hooks/useBalance";
import PointsResult from "../customer/PointsResult";

interface PointsData {
  amount: number;
  value: number;
  transactions: MyTransactionItem[];
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  // Set on the stage-2 assistant turn: the balance card the endpoint returns,
  // rendered below the text instead of being recited in prose.
  points?: PointsData;
  // True when a stage-2 lookup (phone + zipcode) matched no account. The model
  // may still reply "Aquí está tu saldo:", so we render a deterministic
  // not-found notice instead of leaving the turn blank.
  notFound?: boolean;
}

const GREETING =
  "¡Hola! Soy el asistente de puntos de La Dalia. ¿Me compartes tu número de teléfono?";

const MAX_MESSAGE_LENGTH = 500;

const ChatBox = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    { role: "assistant", content: GREETING },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const scrollRef = useRef<HTMLDivElement>(null);
  const didMount = useRef(false);

  useEffect(() => {
    // Skip the initial render so loading the page doesn't move the window.
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    // Scroll only the chat container, not every scrollable ancestor (which
    // scrollIntoView would do, dragging the whole page down).
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, isLoading]);

  const send = async () => {
    const text = input.trim();
    if (!text || isLoading) return;

    const next: ChatMessage[] = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setIsLoading(true);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // The greeting is UI-only; send the real conversation starting from the
        // user's first message. The API is stateless, so the full history goes
        // every turn and the Lambda returns only the final assistant text.
        body: JSON.stringify({ messages: next.slice(1) }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: data.data.reply,
          points: data.data.points ?? undefined,
          notFound: data.data.notFound ?? false,
        },
      ]);
    } catch {
      toast({
        title: "Error de conexión",
        description: "No pude procesar tu mensaje. Intenta de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-[400px] lg:h-[500px] rounded-3xl border shadow-strong bg-card">
      <div className="flex items-center gap-2 border-b px-4 py-3">
        <div className="h-2.5 w-2.5 rounded-full bg-amber-500" />
        <span className="font-semibold text-amber-800">Dal.IA, tu asistente de puntos</span>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((m, i) => (
          <div key={i}>
            <div
              className={
                m.role === "user" ? "flex justify-end" : "flex justify-start"
              }
            >
              <div
                className={
                  m.role === "user"
                    ? "max-w-[80%] rounded-2xl bg-primary px-4 py-2 text-sm text-primary-foreground"
                    : "max-w-[80%] rounded-2xl bg-amber-100 px-4 py-2 text-sm text-amber-900"
                }
              >
                {m.content}
              </div>
            </div>
            {m.points && (
              <PointsResult
                amount={m.points.amount}
                value={m.points.value}
                transactions={m.points.transactions}
              />
            )}
            {!m.points && m.notFound && (
              <div className="mt-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                No encontramos una cuenta con esos datos. Verifica que el número
                de teléfono y el código postal sean correctos e inténtalo de
                nuevo.
              </div>
            )}
          </div>
        ))}
        {isLoading && (
          <div className="px-2 text-sm text-muted-foreground">Escribiendo…</div>
        )}
      </div>

      <form
        className="flex gap-2 border-t p-3"
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
      >
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Escribe tu mensaje…"
          disabled={isLoading}
          maxLength={MAX_MESSAGE_LENGTH}
          aria-label="Mensaje"
        />
        <Button type="submit" size="icon" disabled={isLoading || !input.trim()}>
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
};

export default ChatBox;
